/**
 * PlaceholderBars — faint 28-bar histogram watermark sized to match the
 * `CondensedBars` slot (400 × 20 viewBox). Heights are hand-tuned to
 * suggest "data could go here" without resembling any specific shape —
 * same pattern + intent as `KpiSparkBars` in RumPlaceholders.
 *
 * One brand-blue fill at 25% opacity so the visual rhymes with the rest
 * of the empty-state vocabulary across the dashboard.
 *
 * Use cases:
 *   - `PerformanceEmptyTile` (real-user CWV tiles before pixel install)
 *   - `MetricTile` empty-state fallback (replaces the blank `<svg/>` the
 *     `CondensedBars` primitive emits when its `data` array is empty)
 *
 * Shape is identical to the populated chart slot so swapping in real
 * bars when data lands does not reflow surrounding layout.
 */
import type { ReactElement } from 'react';

const HEIGHTS = [
  8, 10, 9, 11, 10, 12, 11, 13, 12, 13, 14, 13, 14, 14, 13, 14, 14, 15, 14, 14, 13, 14, 13, 12, 13,
  12, 11, 10,
];
const WIDTH = 400;
const HEIGHT = 20;
const BAR_WIDTH = 8;
const STEP = (WIDTH - BAR_WIDTH) / (HEIGHTS.length - 1);

export function PlaceholderBars(): ReactElement {
  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Placeholder bar histogram"
      className="block h-5 w-full"
    >
      <title>Placeholder bars — data will populate here</title>
      {HEIGHTS.map((h, i) => (
        <rect
          // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional + count-stable
          key={i}
          x={i * STEP}
          y={HEIGHT - h}
          width={BAR_WIDTH}
          height={h}
          fill="var(--color-bar)"
          opacity={0.25}
        />
      ))}
    </svg>
  );
}
